import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaRedo,
  FaTrashAlt,
  FaTrashRestore,
  FaClock,
  FaFilter,
  FaInfoCircle,
} from "react-icons/fa";
import api from "../api";
import { useAuth } from "../AuthContext";
import { canManageTrash } from "../utils/permissions";

const TYPE_LABELS = {
  form: "Formulários",
  process: "Processos",
  request: "Solicitações",
  file: "Arquivos",
  folder: "Categorias",
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const normalizeType = (type) => TYPE_LABELS[type] || type;

export default function TrashBin({ refreshKey }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [allowedTypes, setAllowedTypes] = useState([]);
  const [typeFilter, setTypeFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [restoringId, setRestoringId] = useState(null);
  const [removingId, setRemovingId] = useState(null);

  const hasAccess = canManageTrash(user);

  const sortedTypes = useMemo(() => {
    const base = allowedTypes || [];
    return base
      .map((type) => ({ value: type, label: normalizeType(type) }))
      .sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));
  }, [allowedTypes]);

  const fetchItems = useCallback(
    async (selectedType) => {
      if (!hasAccess) {
        setLoading(false);
        setItems([]);
        setError("Você não tem permissão para acessar a lixeira.");
        return;
      }
      setLoading(true);
      setError("");
      try {
        const params = {};
        if (selectedType && selectedType !== "all") {
          params.type = selectedType;
        }
        const res = await api.get("/trash", { params });
        setItems(res.data?.items || []);
        setAllowedTypes(res.data?.allowedTypes || []);
      } catch (err) {
        const message = err.response?.data?.error || "Não foi possível carregar a lixeira.";
        setError(message);
        setItems([]);
      } finally {
        setLoading(false);
      }
    },
    [hasAccess],
  );

  useEffect(() => {
    fetchItems(typeFilter);
  }, [fetchItems, typeFilter, refreshKey]);

  const handleRefresh = useCallback(() => {
    fetchItems(typeFilter);
  }, [fetchItems, typeFilter]);

  const handleRestore = useCallback(
    async (item) => {
      if (!item) return;
      if (!window.confirm(`Deseja restaurar "${item.resourceLabel || item.resourceId}"?`)) {
        return;
      }
      setRestoringId(item.id);
      try {
        await api.post(`/trash/${item.id}/restore`);
        alert("Item restaurado com sucesso.");
        fetchItems(typeFilter);
      } catch (err) {
        const message = err.response?.data?.error || "Não foi possível restaurar este item.";
        alert(message);
      } finally {
        setRestoringId(null);
      }
    },
    [fetchItems, typeFilter],
  );

  const handleRemove = useCallback(
    async (item) => {
      if (!item) return;
      if (
        !window.confirm(
          `Deseja remover definitivamente "${item.resourceLabel || item.resourceId}"? Essa ação não pode ser desfeita.`,
        )
      ) {
        return;
      }
      setRemovingId(item.id);
      try {
        await api.delete(`/trash/${item.id}`);
        alert("Item excluído definitivamente.");
        fetchItems(typeFilter);
      } catch (err) {
        const message = err.response?.data?.error || "Não foi possível excluir este item.";
        alert(message);
      } finally {
        setRemovingId(null);
      }
    },
    [fetchItems, typeFilter],
  );

  const activeFilter = typeFilter === "all" ? null : typeFilter;

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">Lixeira</h1>
          <p className="text-sm text-slate-500">
            Itens excluídos permanecem disponíveis para recuperação por 30 dias antes da remoção definitiva.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
            <FaFilter className="text-slate-500" />
            <select
              className="rounded-md border border-slate-200 bg-white px-2 py-1 text-sm text-slate-700 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-200"
              value={typeFilter}
              onChange={(event) => setTypeFilter(event.target.value)}
              disabled={loading || !sortedTypes.length}
            >
              <option value="all">Todos os tipos</option>
              {sortedTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            onClick={handleRefresh}
            className="inline-flex items-center gap-2 rounded-xl border border-indigo-500 px-4 py-2 text-sm font-medium text-indigo-600 transition hover:bg-indigo-50"
            disabled={loading}
          >
            <FaRedo className={loading ? "animate-spin" : ""} />
            Atualizar
          </button>
        </div>
      </header>

      {error ? (
        <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <FaInfoCircle className="mt-0.5 text-red-500" />
          <div>{error}</div>
        </div>
      ) : null}

      <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Itens excluídos</h2>
            <p className="text-xs text-slate-500">
              {loading
                ? "Carregando itens da lixeira..."
                : items.length === 0
                ? "Nenhum item encontrado para o filtro selecionado."
                : `${items.length} item${items.length > 1 ? "s" : ""} na lixeira.`}
            </p>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-100">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Tipo
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Identificador
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Removido em
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Expira em
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Informações
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-sm text-slate-500">
                    <div className="inline-flex items-center gap-2 rounded-full border border-indigo-200 bg-indigo-50 px-4 py-2 text-indigo-600">
                      <FaClock className="animate-spin" /> Carregando lixeira...
                    </div>
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-sm text-slate-500">
                    <FaTrashAlt className="mx-auto mb-2 text-xl text-slate-400" />
                    Nenhum item encontrado na lixeira.
                  </td>
                </tr>
              ) : (
                items.map((item) => (
                  <tr key={item.id} className="hover:bg-slate-50/70">
                    <td className="px-6 py-4 align-top text-sm font-medium text-slate-700">
                      {normalizeType(item.resourceType)}
                    </td>
                    <td className="px-6 py-4 align-top text-sm text-slate-600">
                      <div className="flex flex-col">
                        <span className="font-semibold text-slate-700">{item.resourceLabel || "Sem título"}</span>
                        <span className="text-xs text-slate-400">{item.resourceId}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 align-top text-sm text-slate-600">
                      {formatDateTime(item.deletedAt)}
                    </td>
                    <td className="px-6 py-4 align-top text-sm text-slate-600">
                      {formatDateTime(item.expiresAt)}
                    </td>
                    <td className="px-6 py-4 align-top text-xs text-slate-500">
                      {item.metadata ? (
                        <div className="space-y-1">
                          {Object.entries(item.metadata).map(([key, value]) => (
                            <div key={key} className="flex gap-2">
                              <span className="font-semibold capitalize text-slate-600">{key}:</span>
                              <span className="text-slate-500">
                                {typeof value === "string" || typeof value === "number"
                                  ? String(value)
                                  : JSON.stringify(value)}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span>-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 align-top text-right text-sm">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => handleRestore(item)}
                          className="inline-flex items-center gap-2 rounded-lg border border-emerald-500 px-3 py-1 text-xs font-semibold text-emerald-600 transition hover:bg-emerald-50"
                          disabled={restoringId === item.id || removingId === item.id}
                        >
                          <FaTrashRestore /> Restaurar
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemove(item)}
                          className="inline-flex items-center gap-2 rounded-lg border border-red-500 px-3 py-1 text-xs font-semibold text-red-600 transition hover:bg-red-50"
                          disabled={restoringId === item.id || removingId === item.id}
                        >
                          <FaTrashAlt /> Excluir
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
